import React, { ReactNode, useEffect, useState } from 'react'

import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Switch from 'react-switch'
import ReactTooltip from 'react-tooltip'

import { getColor } from '@/key-components/utils/KeyColors'
import { useGetLoggedUserQuery, useSetUserControlledFeatureFlagMutation } from '@/services/user'

export type NavItemProps = {
  path: string
  label: string
  enabled?: boolean
  hidden?: boolean
  icon?: ReactNode
  dataTest?: string
}

export type NavProps = {
  items: Array<NavItemProps>
}

const NavItem = ({ label, path, enabled, icon, dataTest }: NavItemProps) => {
  const router = useRouter()
  const navItemClass = classNames({
    'text-sky-400': router.asPath === path,
    'hover:text-blue-600': true,
  })

  return (
    <li
      data-test={dataTest}
      className={navItemClass}
    >
      <Link
        href={enabled ? path : '#'}
        className='flex items-center group'
      >
        {icon && <span data-tip={label}>{icon}</span>}
      </Link>
    </li>
  )
}

export const NavSidebar = ({ items }: NavProps) => {
  const router = useRouter()

  const { data: user } = useGetLoggedUserQuery()

  const [isMounted, setIsMounted] = useState(false)
  const [setUserControlledFF] = useSetUserControlledFeatureFlagMutation()

  const isUserControlledFF = user?.data?.is_user_controller_feature_flag_on

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <div className='bg-white relative'>
      <div
        className='sticky'
        style={{ top: '200px', zIndex: 29 }}
      >
        <div
          style={{
            width: 80,
          }}
        >
          {isMounted && <ReactTooltip place={'right'} />}
          <nav className='flex flex-col justify-between h-[29rem]'>
            <ul
              className='flex flex-col py-10 gap-y-8 items-center justify-center'
              data-test='left-nav-bar-container'
            >
              {items.map((item, i) => {
                if (item.hidden) return null

                return (
                  <NavItem
                    key={i}
                    label={item.label}
                    path={item.path}
                    enabled={item.enabled}
                    hidden={item.hidden}
                    icon={item.icon}
                    dataTest={item.dataTest}
                  />
                )
              })}
            </ul>
          </nav>
          {isMounted && (
            <ReactTooltip
              place={'right'}
              id='user-controlled-feature-flag'
            >
              {`Turn ${user?.data?.is_user_controller_feature_flag_on ? `Off` : `On`} Feature Flag`}
            </ReactTooltip>
          )}
          <div
            className='flex items-center justify-center h-min'
            data-tip
            data-test='is-user-controlled-ff-switch'
            data-for='user-controlled-feature-flag'
          >
            <Switch
              className='react-switch'
              boxShadow='0px 1px 5px rgba(0, 0, 0, 0.6)'
              activeBoxShadow='0px 0px 1px 10px rgba(0, 0, 0, 0.2)'
              height={15}
              width={40}
              onColor='#aabccf'
              onHandleColor={getColor('blue')}
              handleDiameter={18}
              uncheckedIcon={false}
              checkedIcon={false}
              onChange={() => setUserControlledFF()}
              checked={isUserControlledFF ? isUserControlledFF : false}
            />
          </div>
        </div>

        <div
          onClick={() => router.push(process.env.NEXT_PUBLIC_WORKVIEWS_URL)}
          title='Navigate to Work Views'
          data-test='navigate-to-work-views'
          className='absolute top-[20px] right-[-15px] flex w-[30px] h-[30px] cursor-pointer rounded-full bg-sky-400 text-white items-center justify-center'
        >
          <ChevronLeftIcon width={16} />
        </div>
      </div>
    </div>
  )
}
